import {Root} from "./TileDeleteButton.styles";

export type TileDeleteButtonProps = {
	/** The control's accessible name, from the caller's catalog ("Delete image N permanently"). */
	label: string;
	/** Pointer tooltip: why the delete is available, or why it is refused while the image is live. */
	title?: string;
	disabled?: boolean;
	onClick: () => void;
};

/**
 * The PERMANENTLY destructive tile control — it deletes the picture from the sender's library for good
 * (api#471). It was the styled `DeleteButton` primitive until ui#766 made it a component of the shared
 * tile vocabulary, paired with {@link ./TileRemoveButton} the way the two gestures pair on a library.
 *
 * Its glyph is a bin where the reversible control's is a ✕ (ui#766): a library can render both
 * controls a few pixels apart — a selected tile's overlay ✕ beside an unselected tile's row control —
 * and position and fill weight alone were the only cue separating "take out of the selection" from
 * "destroy the sender's image for good". An irreversible action does not share a glyph with an
 * undoable one.
 *
 * Named for what it does: `RemoveButton` until ui#760 made the *reversible* gesture the one that reads
 * as "remove". Dumb by mandate (epic #26): the caller's hook decides whether deletion is allowed and
 * passes `disabled` with a `title` saying why.
 */
export function TileDeleteButton({label, title, disabled, onClick}: TileDeleteButtonProps) {
	return (
		<Root aria-label={label} title={title} disabled={disabled} onClick={onClick}>
			{/* Decorative, like the tile's other glyph controls — `aria-label` names the action. */}
			<svg width={13} height={13} viewBox="0 0 24 24" fill="none" stroke="currentColor"
				 strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
				<polyline points="3 6 5 6 21 6"/>
				<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
			</svg>
		</Root>
	);
}
